/**
 * Loading Component
 *
 * Accessible loading spinner with customizable sizes and variants.
 * Respects prefers-reduced-motion for accessibility.
 *
 * WCAG 2.1 Level AA + BFSG konform
 */

import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../../lib/utils';

export interface LoadingProps extends ComponentPropsWithoutRef<'div'> {
  /**
   * Size of the spinner
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Variant/color of the spinner
   * @default 'default'
   */
  variant?: 'default' | 'primary' | 'secondary' | 'white';
  /**
   * Loading text to display
   */
  label?: string;
  /**
   * Center the spinner in its container
   * @default false
   */
  centered?: boolean;
  /**
   * Show as overlay (fullscreen)
   * @default false
   */
  overlay?: boolean;
}

/**
 * Loading Component
 *
 * @example
 * ```tsx
 * <Loading size="lg" label="Lädt..." />
 * <Loading variant="primary" centered />
 * <Loading overlay label="Daten werden geladen..." />
 * ```
 */
const Loading = forwardRef<HTMLDivElement, LoadingProps>(
  (
    {
      className,
      size = 'md',
      variant = 'default',
      label,
      centered = false,
      overlay = false,
      ...props
    },
    ref
  ) => {
    const sizeClasses = {
      sm: 'h-4 w-4',
      md: 'h-6 w-6',
      lg: 'h-8 w-8',
      xl: 'h-12 w-12',
    };

    const variantClasses = {
      default: 'text-neutral-600 dark:text-neutral-400',
      primary: 'text-primary-600 dark:text-primary-400',
      secondary: 'text-primary-600 dark:text-primary-400',
      white: 'text-white',
    };

    const content = (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        aria-label={label || 'Loading'}
        className={cn(
          'inline-flex flex-col items-center gap-3',
          centered && 'justify-center',
          className
        )}
        {...props}
      >
        <Loader2
          className={cn(
            'animate-spin',
            sizeClasses[size],
            variantClasses[variant],
            // Respect prefers-reduced-motion
            'motion-reduce:animate-[spin_1.5s_linear_infinite]'
          )}
          aria-hidden="true"
        />
        {label && (
          <span className={cn('text-sm font-medium', variantClasses[variant])}>
            {label}
          </span>
        )}
        {/* Screen reader only text */}
        <span className="sr-only">{label || 'Loading...'}</span>
      </div>
    );

    if (overlay) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          {content}
        </div>
      );
    }

    if (centered) {
      return (
        <div className="flex min-h-[200px] w-full items-center justify-center">
          {content}
        </div>
      );
    }

    return content;
  }
);

Loading.displayName = 'Loading';

/**
 * Spinner Component (alias for Loading without label)
 */
const Spinner = forwardRef<HTMLDivElement, Omit<LoadingProps, 'label'>>(
  ({ ...props }, ref) => <Loading ref={ref} {...props} />
);

Spinner.displayName = 'Spinner';

export { Loading, Spinner };
